import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { RefreshCw, Building2, CheckCircle2, AlertTriangle, ChevronDown } from 'lucide-react';
import { ResultGroups } from '@/components/LeadListings';
import { useAuth } from '@/contexts/AuthContext';

const fmtDate = iso => (iso ? new Date(iso).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : 'nunca');

const SourcesStatus = () => {
  const [st, setSt] = useState(null);
  const load = () => api.get('/admin/external-listings/status').then(setSt).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!st?.running) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [st?.running]);

  const sync = async () => {
    try {
      const r = await api.post('/admin/external-listings/sync');
      toast.success(r.status === 'already_running' ? 'Ya se está actualizando' : 'Actualización iniciada; toma unos minutos');
      load(); setSt(prev => ({ ...prev, running: true }));
    } catch { toast.error('No se pudo iniciar la actualización'); }
  };

  if (!st) return null;
  return (
    <div data-testid="external-sources-status" className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-white/50">Última lectura: <span className="text-white/80">{fmtDate(st.last_run_at)}</span> · se actualiza sola todos los días a las 6 a. m.</p>
        <button onClick={sync} disabled={st.running} data-testid="external-sync-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-white/70 hover:text-white disabled:opacity-50">
          <RefreshCw size={12} className={st.running ? 'animate-spin' : ''} />{st.running ? 'Actualizando...' : 'Actualizar ahora'}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {st.source_order.map(k => {
          const s = st.sources[k];
          return (
            <div key={k} data-testid={`external-source-${k}`} className="flex items-center justify-between gap-2 text-[11px] px-2.5 py-1.5 rounded-lg bg-white/[0.03]">
              <span className="flex items-center gap-1.5 text-white/70 truncate">
                {!s ? <span className="w-3 h-3 rounded-full border border-white/20" /> : s.ok ? <CheckCircle2 size={12} className="text-[#6BCB77]" /> : <AlertTriangle size={12} className="text-amber-400" />}
                {s?.name || k}
              </span>
              <span className={`tabular-nums shrink-0 ${s && !s.ok ? 'text-amber-400' : 'text-white/40'}`} title={s?.error || ''}>
                {!s ? 'sin leer' : s.ok ? `${s.count} inmuebles` : 'falló'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ExternalListingsSection = ({ external, features }) => {
  const [showStatus, setShowStatus] = useState(false);
  const { user } = useAuth();
  return (
    <div data-testid="external-listings" className="mt-6 pt-5 border-t border-dashed border-white/10 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
        <div>
          <h3 className="font-outfit text-lg font-medium text-white flex items-center gap-2">
            <Building2 size={17} className="text-[#A38560]" />Otras inmobiliarias
          </h3>
          <p className="text-xs text-white/40 mt-1">Inmuebles publicados hoy en las páginas de brokers aliados (Bogotá y Sabana), con los mismos filtros.</p>
        </div>
        {user?.role === 'admin' && <button onClick={() => setShowStatus(v => !v)} data-testid="external-status-toggle" className="flex items-center gap-1 text-xs text-white/50 hover:text-white self-start">
          Estado de las fuentes <ChevronDown size={13} className={`transition-transform ${showStatus ? 'rotate-180' : ''}`} />
        </button>}
      </div>
      {showStatus && <SourcesStatus />}
      <p data-testid="external-listings-count" className="text-xs text-white/50">{external.items.length} inmueble(s) encontrados</p>
      {external.items.length === 0
        ? <p className="text-sm text-white/40 py-6 text-center">{external.total_indexed ? 'Ningún inmueble externo coincide con esta búsqueda.' : 'Aún no se han leído las páginas de otras inmobiliarias.'}</p>
        : <ResultGroups items={external.items} features={features} prefix="external" />}
    </div>
  );
};
